import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Snackbar,
  Autocomplete
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  VpnKey as VpnKeyIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';

const THEME_COLOR = '#064d51';
const THEME_COLOR_DARK = '#053a3d';
const THEME_GRADIENT = 'linear-gradient(135deg, #064d51 0%, #0b6b70 100%)';

const TcUserPasswordReset = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const [typingCenters, setTypingCenters] = React.useState([]);
  const [loadingTcs, setLoadingTcs] = React.useState(false);
  const [selectedTc, setSelectedTc] = React.useState(null);

  const [form, setForm] = React.useState({
    usernameOrEmail: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [touched, setTouched] = React.useState({
    tc: false,
    usernameOrEmail: false,
    newPassword: false,
    confirmPassword: false
  });
  const [show, setShow] = React.useState({ new: false, confirm: false });

  React.useEffect(() => {
    let active = true;
    setLoadingTcs(true);
    ApiService.getTypingCenters()
      .then((res) => {
        if (!active) return;
        const list = res?.data?.data || res?.data || [];
        setTypingCenters(Array.isArray(list) ? list : []);
      })
      .catch((err) => console.error('Failed to load typing centers:', err))
      .finally(() => { if (active) setLoadingTcs(false); });
    return () => { active = false; };
  }, []);

  const clearNotices = () => { setError(''); setSuccess(''); };

  const validate = () => {
    if (!selectedTc) return 'Select a typing center';
    if (!form.usernameOrEmail.trim()) return 'Username or email is required';
    if (!form.newPassword) return 'New password is required';
    if (form.newPassword.length < 6) return 'New password must be at least 6 characters';
    if (!form.confirmPassword) return 'Please confirm the new password';
    if (form.newPassword !== form.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }

    if (!window.confirm(
      `Reset the password for "${form.usernameOrEmail.trim()}" in ${selectedTc.name || 'this typing center'}?`
    )) return;

    setLoading(true);
    clearNotices();
    try {
      await ApiService.post('/api/users/admin/reset-tc-password', {
        typingCenterId: selectedTc.id,
        usernameOrEmail: form.usernameOrEmail.trim(),
        password: form.newPassword
      });
      setSuccess('Typing-center user password reset successfully');
      setForm({ usernameOrEmail: '', newPassword: '', confirmPassword: '' });
      setTouched({ tc: false, usernameOrEmail: false, newPassword: false, confirmPassword: false });
    } catch (e2) {
      setError(e2?.response?.data?.error || e2?.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const isValid =
    selectedTc &&
    form.usernameOrEmail.trim() &&
    form.newPassword &&
    form.confirmPassword &&
    form.newPassword === form.confirmPassword &&
    form.newPassword.length >= 6;

  const fieldLabelSx = { fontWeight: 600, mb: 1, color: 'text.secondary' };
  const roundedInputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, height: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          height: '100%',
          minHeight: 500
        }}
      >
        <Grid container sx={{ height: '100%' }}>
          {/* Left informational panel */}
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              background: THEME_GRADIENT,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 4,
              color: '#fff',
              minHeight: { xs: 200, md: 'auto' }
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3
              }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
              Reset TC Password
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', opacity: 0.9, maxWidth: 300 }}>
              Platform-admin action to reset a typing-center user's password
            </Typography>
            <Box sx={{ mt: 4 }}>
              {[
                'Select the typing center',
                'Enter the user\'s username or email',
                'Only TC users can be reset (not portal / group / platform)',
                'Minimum 6 characters'
              ].map((line) => (
                <Box key={line} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>{line}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Right form panel */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              p: { xs: 3, md: 5 }
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
              Reset User Credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Choose the typing center, identify the user, and set a new password
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={onSubmit}>
              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={fieldLabelSx}>Typing Center</Typography>
                <Autocomplete
                  options={typingCenters}
                  loading={loadingTcs}
                  value={selectedTc}
                  onChange={(e, v) => { setSelectedTc(v); clearNotices(); }}
                  onBlur={() => setTouched((t) => ({ ...t, tc: true }))}
                  getOptionLabel={(o) => (o?.code ? `${o.name} (${o.code})` : (o?.name || ''))}
                  isOptionEqualToValue={(o, v) => o?.id === v?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select typing center"
                      error={touched.tc && !selectedTc}
                      helperText={touched.tc && !selectedTc ? 'Required' : ''}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <BusinessIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {loadingTcs ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  sx={roundedInputSx}
                />
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={fieldLabelSx}>Username or Email</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter the user's username or email"
                  value={form.usernameOrEmail}
                  onChange={(e) => { setForm((p) => ({ ...p, usernameOrEmail: e.target.value })); clearNotices(); }}
                  onBlur={() => setTouched((t) => ({ ...t, usernameOrEmail: true }))}
                  error={touched.usernameOrEmail && !form.usernameOrEmail.trim()}
                  helperText={touched.usernameOrEmail && !form.usernameOrEmail.trim() ? 'Required' : ''}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                      </InputAdornment>
                    )
                  }}
                  sx={roundedInputSx}
                />
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={fieldLabelSx}>New Password</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={show.new ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={form.newPassword}
                  onChange={(e) => { setForm((p) => ({ ...p, newPassword: e.target.value })); clearNotices(); }}
                  onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
                  error={touched.newPassword && (!form.newPassword || form.newPassword.length < 6)}
                  helperText={
                    touched.newPassword && !form.newPassword
                      ? 'Required'
                      : touched.newPassword && form.newPassword.length < 6
                        ? 'Min 6 characters'
                        : ''
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShow((s) => ({ ...s, new: !s.new }))} edge="end" size="small">
                          {show.new ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={roundedInputSx}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={fieldLabelSx}>Confirm New Password</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type={show.confirm ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  value={form.confirmPassword}
                  onChange={(e) => { setForm((p) => ({ ...p, confirmPassword: e.target.value })); clearNotices(); }}
                  onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
                  error={touched.confirmPassword && (!form.confirmPassword || form.confirmPassword !== form.newPassword)}
                  helperText={
                    touched.confirmPassword && !form.confirmPassword
                      ? 'Required'
                      : touched.confirmPassword && form.confirmPassword !== form.newPassword
                        ? 'Passwords do not match'
                        : ''
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShow((s) => ({ ...s, confirm: !s.confirm }))} edge="end" size="small">
                          {show.confirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={roundedInputSx}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !isValid}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VpnKeyIcon />}
                sx={{
                  py: 1.3,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  bgcolor: THEME_COLOR,
                  color: '#ffffff',
                  boxShadow: '0 4px 14px rgba(6,77,81,0.35)',
                  '&:hover': { bgcolor: THEME_COLOR_DARK },
                  '&:disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' }
                }}
              >
                {loading ? 'Submitting...' : 'Reset Password'}
              </Button>
            </form>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ borderRadius: 2 }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TcUserPasswordReset;
